import {useAuth} from '../hooks/useAuth';
import {api} from '../../api';
import {useNavigate} from 'react-router-dom';
import {useNotifyHook} from '../hooks/useNotify';
import {useState, useEffect, useCallback, useRef} from 'react';
import {format} from 'date-fns';
import ReviewModal from '../components/ReviewModal';

const MyBookings = () => {
    const {user, isAuthenticated} = useAuth();
    const navigate = useNavigate();
    const notify = useNotifyHook();
    const [myBookings, setMyBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [cancellingId, setCancellingId] = useState(null);
    const [statusFilter, setStatusFilter] = useState('confirmed');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalBookings, setTotalBookings] = useState(0);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedBookingForReview, setSelectedBookingForReview] = useState(null);

    // New states for cancellation flow
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [bookingToCancel, setBookingToCancel] = useState(null);
    const [refundData, setRefundData] = useState(null);
    const [calculatingRefund, setCalculatingRefund] = useState(false);

    // Use ref to prevent multiple fetches
    const isFetchingRef = useRef(false);
    const abortControllerRef = useRef(null);

    // Memoized fetch function with abort controller
    const fetchBookings = useCallback(async (page = 1, status = 'confirmed') => {
        if (isFetchingRef.current) 
            return;
        


        try {
            isFetchingRef.current = true;
            setLoading(true);

            // Create new abort controller
            abortControllerRef.current = new AbortController();

            const requestBody = {
                status: status === 'all' ? 'all' : status,
                page: page
            };

            const response = await fetch(api.myBookings, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${
                        localStorage.getItem('token') || ''
                    }`
                },
                body: JSON.stringify(requestBody),
                signal: abortControllerRef.current.signal
            });

        

            const result = await response.json();

            if (result.status) {
                const bookings = result.data ?. data || [];

                setTotalPages(result.data ?. last_page || 1);
                setTotalBookings(result.data ?. total || 0);
                setCurrentPage(result.data ?. current_page || 1);

                setMyBookings(bookings);
            } else {
                throw new Error(result.message || 'Failed to fetch bookings');
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                notify.error(error.message || 'Failed to fetch bookings');
                setMyBookings([]);
            }
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [notify]);

    const handleStatusFilterChange = (status) => {
        setStatusFilter(status);
        setCurrentPage(1);
        fetchBookings(1, status);
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages || page === currentPage) 
            return;
        


        setCurrentPage(page);
        fetchBookings(page, statusFilter);
    };

    const handleRefresh = () => {
        fetchBookings(currentPage, statusFilter);
    };

    const calculateRefund = async (bookingId, bookingNumber) => {
        try {
            setCalculatingRefund(true);
            const response = await fetch(`${
                api.refundCalculation
            }/${bookingId}/${bookingNumber}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${
                        localStorage.getItem('token') || ''
                    }`
                }
            });

           
            const result = await response.json();

            if (result.status) {
                setRefundData(result.data);
                return result.data;
            } else {
                throw new Error(result.message || 'Failed to calculate refund');
            }
        } catch (error) {
            console.error('Error calculating refund:', error);
            notify.error(error.message || 'Failed to calculate refund');
            return null;
        } finally {
            setCalculatingRefund(false);
        }
    };

    // Initiate cancellation process
    const initiateCancelBooking = async (booking) => {
        setBookingToCancel(booking);

        // Calculate refund first
        const refundInfo = await calculateRefund(booking.id, booking.booking_number);

        if (refundInfo) {
            setRefundData(refundInfo);
            setShowCancelConfirm(true);
        }
    };

    // Confirm and cancel booking
    const confirmCancelBooking = async () => {
        if (!bookingToCancel) 
            return;
        


        try {
            setCancellingId(bookingToCancel.id);
            const response = await fetch(api.cancelBooking, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${
                        localStorage.getItem('token') || ''
                    }`
                },
                body: JSON.stringify(
                    {id: bookingToCancel.id}
                )
            });

          

            const result = await response.json();

            if (result.status) {
                notify.success('Booking cancelled successfully');
                // Refresh current page
                fetchBookings(currentPage, statusFilter);
                setIsModalOpen(false);
                setShowCancelConfirm(false);
                setRefundData(null);
                setBookingToCancel(null);
            } else {
                throw new Error(result.message || 'Failed to cancel booking');
            }
        } catch (error) {
            console.error('Error cancelling booking:', error);
            notify.error(error.message || 'Failed to cancel booking');
        } finally {
            setCancellingId(null);
        }
    };

    // View details
    const handleViewDetails = (booking) => {
        setSelectedBooking(booking);
        setIsModalOpen(true);
    }

    // Open ticket in new window
    const handleOpenTicket = (url) => {
        if (url) {
            const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
            if (newWindow) 
                newWindow.opener = null;
            


        }
    }

    // Format date
    const formatDate = useCallback((dateString) => {
        try {
                const date = new Date(dateString);

                if (isNaN(date.getTime())) {
                    return dateString;
                }

                const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0 || date.getMilliseconds() !== 0;

                return hasTime ? format(date, 'dd MMM yyyy, hh:mm a') : format(date, 'dd MMM yyyy');

            } catch {
                return dateString;
            }},
        []
    );

    // Format currency
        const formatCurrency = useCallback((amount) => {
        const numAmount = parseFloat(amount || 0);
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(numAmount);
    }, []);

    // Get status badge class
        const getStatusBadgeClass = useCallback((status) => {
        const statusMap = {
            'confirmed': 'bg-green-100 text-green-800',

            'cancelled': 'bg-red-100 text-red-800',
            'completed': 'bg-blue-100 text-blue-800'
        };

        const statusKey = status ?. toLowerCase();
        const badgeClass = statusMap[statusKey] || 'bg-gray-100 text-gray-800';

        return `${badgeClass} px-3 py-1 rounded-full text-sm`;
    }, []);

    // Calculate stats from current data
        const calculateStats = useCallback(() => {
        const allBookings = myBookings;
        return {
            total: totalBookings,
            confirmed: allBookings.filter(b => b.status ?. toLowerCase() === 'confirmed').length,
            cancelled: allBookings.filter(b => b.status ?. toLowerCase() === 'cancelled').length
        };
    }, [myBookings, totalBookings]);

    // Generate pagination buttons
        const renderPaginationButtons = useCallback(() => {
        const buttons = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                buttons.push(i);
            }
        } else {
            buttons.push(1);

            let start = Math.max(2, currentPage - 1);
            let end = Math.min(totalPages - 1, currentPage + 1);

            if (currentPage <= 3) {
                end = 4;
            }

            if (currentPage >= totalPages - 2) {
                start = totalPages - 3;
            }

            if (start > 2) {
                buttons.push('...');
            }

            for (let i = start; i <= end; i++) {
                buttons.push(i);
            }

            if (end < totalPages - 1) {
                buttons.push('...');
            }

            buttons.push(totalPages);
        }

        return buttons;
    }, [currentPage, totalPages]);

    const handleAddReview = (booking) => {
        if (booking.review) {
            setSelectedBookingForReview({
                id: booking.id,
                busId: booking.bus_id,
                routeId: booking.route_id,
                vendorId: booking.vendor_id,
                bookingNumber: booking.booking_number,
                busName: booking.bus_name,
                existingReview: booking.review
            });
            setShowReviewModal(true);
        } else {
            setSelectedBookingForReview({
                id: booking.id,
                busId: booking.bus_id,
                routeId: booking.route_id,
                vendorId: booking.vendor_id,
                bookingNumber: booking.booking_number,
                busName: booking.bus_name
            });
            setShowReviewModal(true);
        }
    };
    const handleReviewSubmitted = () => {
        notify.success('Thank you for your review!');

        if (selectedBookingForReview) {
            setMyBookings(prevBookings => prevBookings.map(booking => booking.id === selectedBookingForReview.id ? {
                ...booking,
                is_reviewed: true
            } : booking));

            if (selectedBooking && selectedBooking.id === selectedBookingForReview.id) {
                setSelectedBooking(prev => ({
                    ...prev,
                    is_reviewed: true
                }));
            }
        }

        setShowReviewModal(false);
    };
    // Fetch bookings on component mount
        useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }

        fetchBookings(1, 'confirmed');

        // Cleanup function
        return() => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [navigate]);

    if (!isAuthenticated) {
        return null;
    }

    const stats = calculateStats();

    return (
        <div className="min-h-screen bg-gray-50 py-6">
            <div className="container mx-auto px-4 max-w-7xl">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">My Bookings</h1>
                    <p className="text-gray-600">
                        Welcome back, {
                        user ?. name
                    }! Manage your bookings here.
                    </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    {
                    [
                        {
                            label: 'Total',
                            value: stats.total,
                            bg: 'bg-blue-50',
                            text: 'text-blue-700'
                        }, {
                            label: 'Confirmed',
                            value: stats.confirmed,
                            bg: 'bg-green-50',
                            text: 'text-green-700'
                        }, {
                            label: 'Cancelled',
                            value: stats.cancelled,
                            bg: 'bg-red-50',
                            text: 'text-red-700'
                        },
                    ].map((stat) => (
                        <div key={
                                stat.label
                            }
                            className={
                                `${
                                    stat.bg
                                } rounded-lg p-4 text-center`
                        }>
                            <div className={
                                `text-2xl font-bold ${
                                    stat.text
                                }`
                            }>
                                {
                                stat.value
                            }</div>
                            <div className="text-sm text-gray-600 mt-1">
                                {
                                stat.label
                            }</div>
                        </div>
                    ))
                } </div>

                {/* Filters Only */}
                <div className="bg-white rounded-lg shadow p-4 mb-6">
                    <div className="flex flex-wrap gap-2">
                        {
                        [
                            {
                                value: 'all',
                                label: 'All'
                            }, {
                                value: 'confirmed',
                                label: 'Confirmed'
                            }, {
                                value: 'cancelled',
                                label: 'Cancelled'
                            },
                        ].map((filter) => (
                            <button key={
                                    filter.value
                                }
                                onClick={
                                    () => handleStatusFilterChange(filter.value)
                                }
                                className={
                                    `px-4 py-2 rounded-lg font-medium ${
                                        statusFilter === filter.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                    }`
                            }>
                                {
                                filter.label
                            } </button>
                        ))
                    }
                        <button onClick={handleRefresh}
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                            {
                            loading ? 'Refreshing...' : 'Refresh'
                        } </button>
                    </div>
                </div>

                {/* Bookings Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex justify-between items-center">
                            <h2 className="text-lg font-semibold text-gray-900">
                                Your Bookings ({totalBookings}
                                total)
                            </h2>
                            {
                            totalPages > 1 && (
                                <div className="text-sm text-gray-600">
                                    Page {currentPage}
                                    of {totalPages} </div>
                            )
                        } </div>
                    </div>

                    {
                    loading ? (
                        <div className="py-12 text-center">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <p className="text-gray-600 mt-2">Loading bookings...</p>
                        </div>
                    ) : myBookings.length === 0 ? (
                        <div className="py-12 text-center">
                            <div className="text-gray-400 mb-4">
                                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-700 mb-2">No bookings found</h3>
                            <p className="text-gray-500">
                                {
                                statusFilter === 'all' ? "You haven't made any bookings yet." : `No ${statusFilter} bookings found.`
                            } </p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Booking ID
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Bus & Route
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date & Time
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Amount
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>

                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {
                                        myBookings.map((booking) => (
                                            <tr key={
                                                    booking.id
                                                }
                                                className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        #{
                                                        booking.booking_number || booking.id
                                                    } </div>
                                                    <div className="text-xs text-gray-500">
                                                        Seats: {
                                                        booking.booking_seats ?. map(seat => seat.seat_no).join(', ') || 'N/A'
                                                    } </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {
                                                        booking.bus_name || booking.bus_number || 'N/A'
                                                    } </div>
                                                    <div className="text-xs text-gray-500">
                                                        {
                                                        booking.from_stand_name
                                                    }
                                                        → {
                                                        booking.to_stand_name
                                                    } </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-gray-900">
                                                        {
                                                        formatDate(booking.journey_date)
                                                    } </div>
                                                    <div className="text-xs text-gray-500">
                                                        Booked: {
                                                        formatDate(booking.created_at)
                                                    } </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {
                                                        formatCurrency(booking.total_amount)
                                                    } </div>
                                                    <div className="text-xs text-gray-500">
                                                        {
                                                        booking.payment_status || 'N/A'
                                                    } </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={
                                                        getStatusBadgeClass(booking.status)
                                                    }>
                                                        {
                                                        booking.status ?. toUpperCase()
                                                    } </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex space-x-2">
                                                        <button onClick={
                                                                () => handleViewDetails(booking)
                                                            }
                                                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                                                            View
                                                        </button>
                                                        {
                                                        booking.status ?. toLowerCase() === 'confirmed' && (
                                                            <button onClick={
                                                                    () => initiateCancelBooking(booking)
                                                                }
                                                                disabled={
                                                                    cancellingId === booking.id
                                                                }
                                                                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                                                {
                                                                cancellingId === booking.id ? 'Cancelling...' : 'Cancel'
                                                            } </button>
                                                        )
                                                    } </div>
                                                </td>
                                            </tr>
                                        ))
                                    } </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {
                            totalPages > 1 && (
                                <div className="px-6 py-4 border-t border-gray-200">
                                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                        <div className="text-sm text-gray-700">
                                            Showing {
                                            myBookings.length
                                        }
                                            of {totalBookings}
                                            bookings
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button onClick={
                                                    () => handlePageChange(1)
                                                }
                                                disabled={
                                                    currentPage === 1
                                                }
                                                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                                « First
                                            </button>
                                            <button onClick={
                                                    () => handlePageChange(currentPage - 1)
                                                }
                                                disabled={
                                                    currentPage === 1
                                                }
                                                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                                ‹ Previous
                                            </button>

                                            {
                                            renderPaginationButtons().map((page, index) => (page === '...' ? (
                                                <span key={
                                                        `ellipsis-${index}`
                                                    }
                                                    className="px-2 text-gray-500">...</span>
                                            ) : (
                                                <button key={page}
                                                    onClick={
                                                        () => handlePageChange(page)
                                                    }
                                                    className={
                                                        `px-3 py-1 text-sm rounded min-w-[40px] transition-colors ${
                                                            currentPage === page ? 'bg-blue-600 text-white' : 'border border-gray-300 hover:bg-gray-50'
                                                        }`
                                                }>
                                                    {page} </button>
                                            )))
                                        }

                                            <button onClick={
                                                    () => handlePageChange(currentPage + 1)
                                                }
                                                disabled={
                                                    currentPage === totalPages
                                                }
                                                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                                Next ›
                                            </button>
                                            <button onClick={
                                                    () => handlePageChange(totalPages)
                                                }
                                                disabled={
                                                    currentPage === totalPages
                                                }
                                                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                                Last »
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )
                        } </>
                    )
                } </div>
            </div>


            {
            isModalOpen && selectedBooking && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="px-6 py-4 border-b flex justify-between items-center">
                            <h2 className="text-lg font-semibold text-gray-900">
                                Booking #{
                                selectedBooking.booking_number || selectedBooking.id
                            } </h2>
                            <button onClick={
                                    () => setIsModalOpen(false)
                                }
                                className="text-gray-400 hover:text-gray-600 text-xl">
                                ✕
                            </button>
                        </div>

                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <h3 className="font-medium text-gray-700 mb-3">Journey Details</h3>
                                    <div className="space-y-3 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Bus:</span>
                                            <span className="font-medium">
                                                {
                                                selectedBooking.bus_name
                                            }</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Route:</span>
                                            <span className="font-medium">
                                                {
                                                selectedBooking.from_stand_name
                                            }
                                                → {
                                                selectedBooking.to_stand_name
                                            } </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Journey Date:</span>
                                            <span className="font-medium">
                                                {
                                                formatDate(selectedBooking.journey_date)
                                            }</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Booking Date:</span>
                                            <span className="font-medium">
                                                {
                                                formatDate(selectedBooking.created_at)
                                            }</span>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="font-medium text-gray-700 mb-3">Payment Details</h3>
                                    <div className="space-y-3 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Total Amount:</span>
                                            <span className="font-medium">
                                                {
                                                formatCurrency(selectedBooking.net_amount)
                                            }</span>
                                        </div>
                                        {
                                        selectedBooking.discount_amount > 0 && (
                                            <>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Discount Applied:</span>
                                                    <span className="font-medium text-green-600">
                                                        -{
                                                        formatCurrency(selectedBooking.discount_amount)
                                                    } </span>
                                                </div>

                                                {
                                                selectedBooking.coupon_code && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600">Coupon Code:</span>
                                                        <div className="flex items-center gap-2">
                                                            <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded">
                                                                {
                                                                selectedBooking.coupon_code
                                                            } </span>
                                                        </div>
                                                    </div>
                                                )
                                            } </>
                                        )
                                    }
                                        <div className="flex justify-between border-t pt-2 mt-2">
                                            <span className="text-gray-700 font-medium">Final Amount:</span>
                                            <span className="text-lg font-bold text-blue-600">
                                                {
                                                formatCurrency(selectedBooking.total_amount)
                                            } </span>


                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Status:</span>
                                            <span className={
                                                getStatusBadgeClass(selectedBooking.status)
                                            }>
                                                {
                                                selectedBooking.status ?. toUpperCase()
                                            } </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Payment Status:</span>
                                            <span className="font-medium">
                                                {
                                                selectedBooking.payment_status || 'N/A'
                                            }</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Booking ID:</span>
                                            <span className="font-medium">#{
                                                selectedBooking.booking_number
                                            }</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {
                            selectedBooking.discount_amount > 0 && (
                                <div className="mb-6">
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0">
                                                <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                                </svg>
                                            </div>
                                            <div className="ml-3">
                                                <h3 className="text-sm font-medium text-green-800">
                                                    {
                                                    selectedBooking.coupon_code ? `Coupon "${
                                                        selectedBooking.coupon_code
                                                    }" Applied Successfully!` : 'Discount Applied Successfully!'
                                                } </h3>
                                                <div className="mt-1 text-sm text-green-700">
                                                    <p>
                                                        You saved {
                                                        formatCurrency(selectedBooking.discount_amount)
                                                    }.on this booking.
                                                    </p>
                                                    {
                                                    selectedBooking.coupon_valid_until && (
                                                        <p className="mt-1 text-xs">
                                                            Coupon valid until: {
                                                            formatDate(selectedBooking.coupon_valid_until)
                                                        } </p>
                                                    )
                                                } </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                            {
                            selectedBooking.status ?. toLowerCase() === 'cancelled' && (
                                <div className="mb-6">
                                    <h3 className="font-medium text-gray-700 mb-3">Refund Information</h3>
                                    <div className="bg-gray-50 p-4 rounded">
                                        <div className="space-y-4">
                                            {/* Refund Amount */}
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">Refund Amount:</span>
                                                <span className="text-lg font-bold text-green-600">
                                                    {
                                                    formatCurrency(selectedBooking.refund_amount || 0)
                                                } </span>
                                            </div>

                                            {/* Refund Status */}
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">Refund Status:</span>
                                                <div className="flex items-center gap-2">
                                                    <span className={
                                                        `px-3 py-1 rounded-full text-sm ${
                                                            selectedBooking.refund_status ?. toLowerCase() === 'processed' ? 'bg-green-100 text-green-800' : selectedBooking.refund_status ?. toLowerCase() === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                                                        }`
                                                    }>
                                                        {
                                                        selectedBooking.refund_status ?. toUpperCase() || 'PENDING'
                                                    } </span>
                                                </div>
                                            </div>

                                            {/* Refund Information Message */}
                                            <div className="mt-3 pt-3 border-t border-gray-200">
                                                {
                                                selectedBooking.refund_status ?. toLowerCase() === 'processed' ? (
                                                    <div className="bg-green-50 border border-green-200 rounded p-3">
                                                        <div className="flex items-start">
                                                            <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                                            </svg>
                                                            <div>
                                                                <p className="text-sm text-green-700 font-medium">Refund Processed</p>
                                                                <p className="text-xs text-green-600 mt-1">
                                                                    Your refund is currently being processed. It typically takes 5-7 business days for the refund to reflect in your account, depending on your bank/payment provider.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="bg-blue-50 border border-blue-200 rounded p-3">
                                                        <div className="flex items-start">
                                                            <svg className="w-5 h-5 text-blue-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                                            </svg>
                                                            <div>
                                                                <p className="text-sm text-blue-700 font-medium">Refund Processing</p>
                                                                <p className="text-xs text-blue-600 mt-1">
                                                                    Your refund is currently being processed. It typically takes 5-7 business days for the refund to reflect in your account, depending on your bank/payment provider.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            } </div>

                                            {/* Additional refund details if available */}
                                            {
                                            (selectedBooking.refund_date || selectedBooking.refund_transaction_id) && (
                                                <div className="mt-3 space-y-2 text-sm">
                                                    {
                                                    selectedBooking.refund_date && (
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Refund Date:</span>
                                                            <span className="font-medium">
                                                                {
                                                                formatDate(selectedBooking.refund_date)
                                                            }</span>
                                                        </div>
                                                    )
                                                }
                                                    {
                                                    selectedBooking.refund_transaction_id && (
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Transaction ID:</span>
                                                            <span className="font-medium">
                                                                {
                                                                selectedBooking.refund_transaction_id
                                                            }</span>
                                                        </div>
                                                    )
                                                } </div>
                                            )
                                        } </div>
                                    </div>
                                </div>
                            )
                        }


                            <ReviewModal isOpen={showReviewModal}
                                onClose={
                                    () => setShowReviewModal(false)
                                }
                                bookingId={
                                    selectedBookingForReview ?. id
                                }
                                busId={
                                    selectedBookingForReview ?. bus_id
                                }
                                routeId={
                                    selectedBookingForReview ?. route_id
                                }
                                vendorId={
                                    selectedBookingForReview ?. vendor_id
                                }
                                bookingNumber={
                                    selectedBookingForReview ?. bookingNumber
                                }
                                busName={
                                    selectedBookingForReview ?. busName
                                }
                                onReviewSubmitted={handleReviewSubmitted}
                                existingReview={selectedBookingForReview ?. existingReview}/>
                            {
                            selectedBooking.booking_seats && selectedBooking.booking_seats.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="font-medium text-gray-700 mb-3">Passenger Details</h3>
                                    <div className="bg-gray-50 p-4 rounded">
                                        <div className="space-y-3">
                                            {
                                            selectedBooking.booking_seats.map((seat, index) => (
                                                <div key={
                                                        seat.id
                                                    }
                                                    className="border-b pb-3 last:border-b-0 last:pb-0">
                                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                                        <div>
                                                            <span className="text-gray-600 text-sm">Name:</span>
                                                            <p className="font-medium">
                                                                {
                                                                seat.name
                                                            }</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-600 text-sm">Age:</span>
                                                            <p className="font-medium">
                                                                {
                                                                seat.age
                                                            }</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-600 text-sm">Gender:</span>
                                                            <p className="font-medium">
                                                                {
                                                                seat.gender
                                                            }</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-600 text-sm">Seat No:</span>
                                                            <p className="font-medium">
                                                                {
                                                                seat.seat_no
                                                            }</p>
                                                        </div>
                                                    </div>
                                                    {
                                                    index < selectedBooking.booking_seats.length - 1 && (
                                                        <div className="mt-3"></div>
                                                    )
                                                } </div>
                                            ))
                                        } </div>
                                    </div>
                                </div>
                            )
                        }

                            <div className="flex justify-end space-x-3 pt-4 border-t">
                                {
                                selectedBooking.ticket_url && (
                                    <button onClick={
                                            () => handleOpenTicket(selectedBooking.ticket_url)
                                        }
                                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors">
                                        View Ticket
                                    </button>
                                )
                            }
                                {
                                selectedBooking.status ?. toLowerCase() === 'confirmed' && (
                                    <> {
                                        selectedBooking.review ? (
                                            <button onClick={
                                                    () => handleAddReview(selectedBooking)
                                                }
                                                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors">
                                                View Review
                                            </button>
                                        ) : (
                                            <button onClick={
                                                    () => handleAddReview(selectedBooking)
                                                }
                                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors">
                                                Add Review
                                            </button>
                                        )
                                    } </>
                                )
                            }
                                {
                                selectedBooking.status ?. toLowerCase() === 'confirmed' && (
                                    <button onClick={
                                            () => initiateCancelBooking(selectedBooking)
                                        }
                                        disabled={
                                            cancellingId === selectedBooking.id
                                        }
                                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                        {
                                        cancellingId === selectedBooking.id ? 'Cancelling...' : 'Cancel Booking'
                                    } </button>
                                )
                            }
                                <button onClick={
                                        () => setIsModalOpen(false)
                                    }
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )
        }

            {/* Cancellation Confirmation Modal */}
            {
            showCancelConfirm && bookingToCancel && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-md w-full">
                        <div className="px-6 py-4 border-b flex justify-between items-center">
                            <h2 className="text-lg font-semibold text-gray-900">Cancel Booking Confirmation</h2>
                            <button onClick={
                                    () => {
                                        setShowCancelConfirm(false);
                                        setRefundData(null);
                                        setBookingToCancel(null);
                                    }
                                }
                                className="text-gray-400 hover:text-gray-600 text-xl">
                                ✕
                            </button>
                        </div>

                        <div className="p-6">
                            {
                            calculatingRefund ? (
                                <div className="text-center py-4">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                    <p className="text-gray-600 mt-2">Calculating refund...</p>
                                </div>
                            ) : refundData ? (
                                <>
                                    <div className="mb-6">
                                        <p className="text-gray-700 mb-4">
                                            Are you sure you want to cancel booking
                                            <span className="font-semibold">#{
                                                bookingToCancel.booking_number
                                            }</span>?
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                This action cannot be undone.
                                        </p>

                                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                                            <div className="flex">
                                                <div className="text-yellow-700">
                                                    <p className="font-medium">Cancellation Policy</p>
                                                    <p className="text-sm mt-1">
                                                        Cancel before {
                                                        refundData.cancelBeforeHour ?. toFixed(1)
                                                    }
                                                        hours to get partial refund.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <h3 className="font-medium text-gray-700 mb-3">Refund Calculation</h3>
                                            <div className="space-y-2 text-sm">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Total Amount Paid:</span>
                                                    <span className="font-medium">
                                                        {
                                                        formatCurrency(refundData.totalAmount)
                                                    }</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Convenience Fee:</span>
                                                    <span className="font-medium">
                                                        {
                                                        formatCurrency(refundData.convenience_fee)
                                                    }</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Cancellation Charges ({
                                                        refundData.ratio
                                                    }%):</span>
                                                    <span className="font-medium text-red-600">-{
                                                        formatCurrency(refundData.deducatedAmount)
                                                    }</span>
                                                </div>
                                                <div className="border-t pt-2 mt-2">
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-700 font-medium">Refund Amount:</span>
                                                        <span className="text-green-600 font-bold text-lg">
                                                            {
                                                            formatCurrency(refundData.amount)
                                                        } </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end space-x-3">
                                        <button onClick={
                                                () => {
                                                    setShowCancelConfirm(false);
                                                    setRefundData(null);
                                                    setBookingToCancel(null);
                                                }
                                            }
                                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors">
                                            Go Back
                                        </button>
                                        <button onClick={confirmCancelBooking}
                                            disabled={
                                                cancellingId === bookingToCancel.id
                                            }
                                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                            {
                                            cancellingId === bookingToCancel.id ? 'Cancelling...' : 'Confirm Cancellation'
                                        } </button>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-4">
                                    <p className="text-red-600">Failed to calculate refund. Please try again.</p>
                                    <div className="flex justify-center space-x-3 mt-4">
                                        <button onClick={
                                                () => setShowCancelConfirm(false)
                                            }
                                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors">
                                            Close
                                        </button>
                                    </div>
                                </div>
                            )
                        } </div>
                    </div>
                </div>
            )
        } </div>

    );
};

export default MyBookings;
